import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import ChatInput from "components/ChatInput";
import Main from "layouts/Main";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { answerEndpoint, getChatHistory } from "services/api";
import Message from "./components/Message";
import { useTranslation } from "react-i18next";
import Container from "components/Container";
import { showSnackbar } from "services/snackbarHelper";

const Chatroom = () => {
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const theme = useTheme();
	const { t } = useTranslation(["validation"]);

	const chatRef = React.useRef(null);

	const [inputValue, setInputValue] = React.useState("");
	const [dataset, setDataset] = React.useState(searchParams.get("dataset"));
	const [datasetName, setDatasetName] = React.useState("");
	const [channel, setChannel] = React.useState(id || null);
	const [messageList, setMessageList] = React.useState([]);

	const [isAsking, setIsAsking] = React.useState(false);

	const handleSubmit = () => {
		const question = inputValue || searchParams.get("question");

		const tempMessageList = [...messageList];
		const sendedMessage = {
			sentBy: "user",
			sentTime: new Date().getTime(),
			message: { messageType: "string", messageContent: question },
		};
		setInputValue("");
		setMessageList([...tempMessageList, sendedMessage]);

		tempMessageList.push(sendedMessage);
		setIsAsking(true);

		return answerEndpoint(question, dataset, channel)
			.then((resp) => {
				if (!resp) return;
				if (resp?.error) throw resp;
				chatRef.current && chatRef.current.updateCredit(resp.credit);
				const receivedMessage = {
					sentBy: "assistant",
					sentTime: new Date().getTime(),
					message: { messageType: "string", messageContent: resp.answer },
					contextTitle: resp.contextTitle,
				};
				setMessageList([...tempMessageList, receivedMessage]);

				if (!channel) {
					setChannel(resp.channel);
					navigate(`/app/chat/${resp.channel}`, { replace: true });
				}

				setIsAsking(false);
				return resp;
			})
			.catch((error) => {
				showSnackbar(error.error, "error");

				tempMessageList[tempMessageList.length - 1].status = "error";
				tempMessageList[tempMessageList.length - 1].helperText = error.error;
				setMessageList(tempMessageList);
				setIsAsking(false);
			});
	};

	React.useEffect(() => {
		if (searchParams.get("question")) {
			handleSubmit();
		}
	}, []);
	React.useEffect(() => {
		if (!channel) return;
		getChatHistory(channel).then((resp) => {
			if (!resp || resp?.error) return;
			chatRef.current && chatRef.current.updateCredit(resp.credit);
			setDataset(resp.datasetInfo.datasetId);
			setDatasetName(resp.datasetInfo.datasetName);
			setMessageList(
				resp.messages.map((message) => {
					return {
						id: message.id,
						sentBy: message.sender,
						sentTime: message.time,
						message: { messageType: "string", messageContent: message.message },
						contextTitle: message.contextTitle,
					};
				})
			);
		});
	}, []);
	React.useEffect(() => {
		if (messageList.length === 0) return;

		const target = document.getElementById(messageList[messageList.length - 1].sentTime.toString());

		target.scrollIntoView({ behavior: "smooth" });
	}, [messageList]);

	return (
		<Main title={datasetName}>
			<Box
				height={1}
				display={"flex"}
				flexDirection={"column"}>
				<Box
					display="flex"
					flex="1 1 auto"
					overflow="hidden">
					<Box
						flex="1 1 auto"
						height="100%"
						sx={{ overflowY: "auto" }}>
						<Container>
							{messageList.map((message, index) => (
								<Box
									key={index}
									id={message.sentTime}
									my={1}
									sx={{ display: "flex", flexDirection: "column", alignItems: message.sentBy === "user" ? "flex-end" : "flex-start" }}>
									<Message message={message} />
									{message?.status === "error" && (
										<Typography
											color="error"
											sx={{ mx: 1 }}>
											{t(message?.helperText || "networkError", { ns: "validation" })}
										</Typography>
									)}
								</Box>
							))}
							{isAsking && (
								<Box ml={1}>
									<CircularProgress />
								</Box>
							)}
						</Container>
					</Box>
				</Box>

				<Box
					pt={2}
					width={1}>
					<Container>
						<ChatInput
							ref={chatRef}
							handleSubmit={handleSubmit}
							inputValue={inputValue}
							setInputValue={setInputValue}
						/>
					</Container>
				</Box>
			</Box>
		</Main>
	);
};

export default Chatroom;
